import { Box, Flex, Heading, LaunchIcon, Paragraph, PinIcon, Stack, Status, Container } from "@eworkit/iris";
import React from "react";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useMedia } from "react-use";

import { LazyUserAvatar } from "@/components/avatars";
import { MEDIA_SIZE_MD } from "@/core/media";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { ProfileCompleteness } from "@/data/consultant-profiles/types";
import { formatLocationWithCountryCode } from "@/data/locations";
import { router } from "@/views/router";

import { DashboardProfileCompleteness } from "./DashboardProfileCompleteness";
import { DashboardProfileHints } from "./DashboardProfileHints";

const MAXIMUM_POINTS = 116;

type DashboardConsultantProfileProps = {
  profile: IConsultantProfile;
  completeness?: ProfileCompleteness;
  onProfileUpdate: (profile: IConsultantProfile) => void;
};

export const DashboardConsultantProfile: FC<DashboardConsultantProfileProps> = ({
  profile,
  completeness,
  onProfileUpdate,
}) => {
  const { t } = useTranslation("session");
  const isResponsive = useMedia(MEDIA_SIZE_MD);

  const profileRole = profile.companyRole
    ? t(`COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.${profile.companyRole}`)
    : t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.PRIVATE_PROFILE");

  return (
    <Container>
      <Flex>
        <Stack gap="xs">
          <LazyUserAvatar size="xl" user={profile} />
        </Stack>
        <Box pl="sm" flex={1}>
          <Flex as={Box} pb="xs">
            <Heading size={["h-sm", null, null, "h-md"]}>
              {`${profile.firstName} ${profile.lastName}`}{" "}
              <Status size={isResponsive ? "sm" : "md"}>{profileRole}</Status>
            </Heading>
          </Flex>
          {profile.position.name ? (
            <Paragraph as={Box} size={["p-sm", null, null, "p-md"]} pb="xs">
              {profile.position?.name}
            </Paragraph>
          ) : (
            profile.location && (
              <Flex alignContent="center" pb="xxs">
                <Box alignSelf="center">
                  <PinIcon size="xs" color="vg500" />{" "}
                </Box>
                <Box>
                  <Paragraph color="n700" size={["p-sm", null, null, "p-md"]}>
                    {formatLocationWithCountryCode(profile.location)}
                  </Paragraph>
                </Box>
              </Flex>
            )
          )}
          <DashboardProfileHints profile={profile} onProfileUpdate={onProfileUpdate} />
        </Box>
        <Box>
          <RouterLink to={router.get("consultant-profiles.detail", { consultantProfileId: profile.id }).path}>
            <LaunchIcon className="launch-icon" size="sm" color="n700" />
          </RouterLink>
        </Box>
      </Flex>
      <Box pt="sm" px="xxs">
        <DashboardProfileCompleteness points={completeness?.score} maximum={MAXIMUM_POINTS} />
      </Box>
    </Container>
  );
};
